/**
 * `useAgentArtifacts` — THE DURABLE SURFACE (spec 16.4), behind one seam.
 *
 * Saved questions, pinned answers, share links, starter questions and turn
 * feedback. The research this design rests on is unambiguous: the measured
 * value of a query assistant landed in PERSISTED OBJECTS, not in conversation.
 * So the library is a first-class hook, not an afterthought bolted onto the
 * chat page.
 *
 * ══ WHAT THESE ENDPOINTS DELIBERATELY DO NOT STORE ═════════════════════════
 *
 * 🔴 A saved question stores an INVOCATION — `metric_id`, typed params,
 * dimensions, a date-range spec — and NEVER a number and NEVER a transcript.
 * Re-opening RE-EXECUTES. That is what makes a saved question structurally
 * incapable of becoming the stale figure that contradicts the dashboard, which
 * is the failure that retired other vendors' assistants. Nothing in this file
 * may add a "cached result" field, however convenient it looks.
 *
 * ══ AND WHY THERE IS NO `update` ═══════════════════════════════════════════
 *
 * Re-saving is one POST. An update path would add a second durable-free-text
 * call site and a second owner-binding predicate for no capability at all, so
 * the backend has four handlers and this hook has four calls (spec 16.4
 * primitive 1). A `revoke` is likewise absent: `unpin` IS the revocation,
 * because the share token lives on the pin row — one lifecycle, one row, one
 * act.
 */

import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';

import { adminFetch, AdminApiError } from '@/providers/admin-api';
import type {
  SavedRunBody,
  AnswerBody,
  DateRangeSpec,
  SavedQuestion,
  StarterQuestion,
} from '@/pages/agent/protocol';

const AGENT_BASE = '/v1/admin/agent';

/**
 * The four generic prompts the empty state falls back to when
 * `GET /starters` fails.
 *
 * 🔴 THEY NAME NO METRIC. The live starters are titles the CALLER may actually
 * read, filtered by `canSeeMetric`; a hardcoded fallback naming metric ids
 * would advertise metrics an admin's role withholds and teach them to ask for
 * things that will always refuse. Spec 16.2 state 1: "never invented metric
 * names".
 */
export const FALLBACK_STARTERS: readonly string[] = [
  'How many members signed up each week this quarter?',
  'What is the weekly new match-ready count, and how does it compare with the prior week?',
  'Which onboarding step is blocking the most members right now?',
  'Is any part of the pipeline stale?',
];

export interface SaveQuestionInput {
  label: string;
  metric_id: string;
  params?: Record<string, string | number | boolean>;
  dimensions?: string[];
  date_range_spec?: DateRangeSpec | null;
  visibility?: 'private' | 'team';
}

export interface ArtifactsController {
  starters: StarterQuestion[];
  /** TRUE when `GET /starters` failed — the empty state must SAY so, and must
   *  fall back to prompts that name no metric. Both branches are exit criteria. */
  startersDegraded: boolean;
  startersLoading: boolean;
  saved: SavedQuestion[];
  savedLoading: boolean;
  /** The last error any mutation produced, as a short operator-readable line. */
  error: string | null;
  refresh: () => void;
  saveQuestion: (input: SaveQuestionInput) => Promise<SavedQuestion | null>;
  deleteQuestion: (id: string) => Promise<boolean>;
  pinAnswer: (savedQuestionId: string, title: string) => Promise<string | null>;
  unpinAnswer: (pinId: string) => Promise<boolean>;
  shareAnswer: (pinId: string) => Promise<string | null>;
  recordFeedback: (turnId: string, verdict: 'up' | 'down', note?: string) => Promise<boolean>;
}

function messageOf(error: unknown): string {
  if (error instanceof AdminApiError) {
    const body = error.body as { error?: unknown; message?: unknown } | undefined;
    const code = typeof body?.error === 'string' ? body.error : null;
    if (error.status === 403) {
      return 'You do not hold `agent.answer.write`, which team-visible acts require.';
    }
    if (code === 'member_identifier' || code === 'member_name') {
      return 'That text was refused: it looks like it identifies a member. Nothing was saved.';
    }
    return `${code ?? error.message} (HTTP ${error.status})`;
  }
  return 'The request did not complete.';
}

/**
 * THE ONE DEFINITION OF THE UNPIN REQUEST.
 *
 * Exported as a plain function because the dashboard's pinned tile needs it and
 * must NOT mount this whole hook: the hook fetches the starters and the saved
 * list on mount, which a dashboard has no use for. The hook's own
 * `unpinAnswer` calls this too, so there is one request shape, not two.
 */
export async function unpinPinnedAnswer(
  apiUrl: string,
  pinId: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await adminFetch(`${apiUrl}${AGENT_BASE}/pin`, {
      method: 'DELETE',
      body: JSON.stringify({ id: pinId }),
    });
    return { ok: true };
  } catch (caught) {
    return { ok: false, message: messageOf(caught) };
  }
}

export function useAgentArtifacts(): ArtifactsController {
  const apiUrl = useApiUrl();
  const [starters, setStarters] = useState<StarterQuestion[]>([]);
  const [startersDegraded, setStartersDegraded] = useState(false);
  const [startersLoading, setStartersLoading] = useState(true);
  const [saved, setSaved] = useState<SavedQuestion[]>([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setStartersLoading(true);
      try {
        const response = await adminFetch(`${apiUrl}${AGENT_BASE}/starters`);
        const body = (await response.json()) as { starters?: StarterQuestion[] };
        if (cancelled) return;
        setStarters(Array.isArray(body.starters) ? body.starters : []);
        setStartersDegraded(false);
      } catch {
        if (cancelled) return;
        // DEGRADED, and the screen says so. Silently showing the generic
        // prompts would delete the only permission-filtered discovery
        // affordance in the UI without telling anyone.
        setStarters([]);
        setStartersDegraded(true);
      } finally {
        if (!cancelled) setStartersLoading(false);
      }
    })();

    void (async () => {
      setSavedLoading(true);
      try {
        const response = await adminFetch(`${apiUrl}${AGENT_BASE}/saved-question`);
        const body = (await response.json()) as { questions?: SavedQuestion[] };
        if (cancelled) return;
        setSaved(Array.isArray(body.questions) ? body.questions : []);
      } catch {
        if (cancelled) return;
        setSaved([]);
      } finally {
        if (!cancelled) setSavedLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, reloadToken]);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);

  const saveQuestion = useCallback(
    async (input: SaveQuestionInput): Promise<SavedQuestion | null> => {
      setError(null);
      try {
        const response = await adminFetch(`${apiUrl}${AGENT_BASE}/saved-question`, {
          method: 'POST',
          body: JSON.stringify({
            label: input.label,
            metric_id: input.metric_id,
            params: input.params ?? {},
            dimensions: input.dimensions ?? [],
            date_range_spec: input.date_range_spec ?? null,
            visibility: input.visibility ?? 'private',
          }),
        });
        const row = (await response.json()) as SavedQuestion;
        refresh();
        return row;
      } catch (caught) {
        setError(messageOf(caught));
        return null;
      }
    },
    [apiUrl, refresh],
  );

  const deleteQuestion = useCallback(
    async (id: string): Promise<boolean> => {
      setError(null);
      try {
        await adminFetch(`${apiUrl}${AGENT_BASE}/saved-question/${id}`, { method: 'DELETE' });
        refresh();
        return true;
      } catch (caught) {
        setError(messageOf(caught));
        return false;
      }
    },
    [apiUrl, refresh],
  );

  const pinAnswer = useCallback(
    async (savedQuestionId: string, title: string): Promise<string | null> => {
      setError(null);
      try {
        const response = await adminFetch(`${apiUrl}${AGENT_BASE}/pin`, {
          method: 'POST',
          body: JSON.stringify({ saved_question_id: savedQuestionId, title }),
        });
        const row = (await response.json()) as { id: string };
        refresh();
        return row.id;
      } catch (caught) {
        setError(messageOf(caught));
        return null;
      }
    },
    [apiUrl, refresh],
  );

  const unpinAnswer = useCallback(
    async (pinId: string): Promise<boolean> => {
      setError(null);
      const outcome = await unpinPinnedAnswer(apiUrl, pinId);
      if (!outcome.ok) {
        setError(outcome.message);
        return false;
      }
      refresh();
      return true;
    },
    [apiUrl, refresh],
  );

  const shareAnswer = useCallback(
    async (pinId: string): Promise<string | null> => {
      setError(null);
      try {
        const response = await adminFetch(`${apiUrl}${AGENT_BASE}/answer/${pinId}/share`, {
          method: 'POST',
        });
        const row = (await response.json()) as { path?: string; share_token?: string };
        if (typeof row.path === 'string') return row.path;
        return typeof row.share_token === 'string' ? `/agent/a/${row.share_token}` : null;
      } catch (caught) {
        setError(messageOf(caught));
        return null;
      }
    },
    [apiUrl],
  );

  const recordFeedback = useCallback(
    async (turnId: string, verdict: 'up' | 'down', note?: string): Promise<boolean> => {
      setError(null);
      try {
        await adminFetch(`${apiUrl}${AGENT_BASE}/turn/${turnId}/feedback`, {
          method: 'POST',
          body: JSON.stringify({ verdict, note: note ?? '' }),
        });
        return true;
      } catch (caught) {
        setError(messageOf(caught));
        return false;
      }
    },
    [apiUrl],
  );

  return {
    starters,
    startersDegraded,
    startersLoading,
    saved,
    savedLoading,
    error,
    refresh,
    saveQuestion,
    deleteQuestion,
    pinAnswer,
    unpinAnswer,
    shareAnswer,
    recordFeedback,
  };
}

/**
 * Read one share token. Kept beside the library rather than in `useAgentTurn`:
 * a share is a LIBRARY act, and the share page must not be able to start a
 * turn.
 *
 * 🔴 THE RESPONSE IS RESOLVED UNDER THE **CALLER'S** PERMISSIONS. A recipient
 * who lacks the metric's page key receives a `PermissionRedactedCell` naming
 * the key — not the number, not a 500, and not the sharer's copy — so this
 * function's job is to render less, never to ask for more.
 */
export function useSharedAnswer(token: string | undefined): {
  answer: AnswerBody | null;
  loading: boolean;
  notFound: boolean;
  error: string | null;
  reload: () => void;
} {
  const apiUrl = useApiUrl();
  const [answer, setAnswer] = useState<AnswerBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminFetch(`${apiUrl}${AGENT_BASE}/answer/${token}`);
        const body = (await response.json()) as AnswerBody;
        if (cancelled) return;
        setAnswer(body);
        setNotFound(false);
      } catch (caught) {
        if (cancelled) return;
        // 404 is the SINGLE answer for "unknown token" and "revoked pin"; the
        // backend makes them byte-identical on purpose so tokens cannot be
        // enumerated by response shape, and the UI must not invent a
        // distinction the API refuses to make.
        if (caught instanceof AdminApiError && caught.status === 404) setNotFound(true);
        else setError(messageOf(caught));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token, reloadToken]);

  return {
    answer,
    loading,
    notFound,
    error,
    reload: useCallback(() => setReloadToken((n) => n + 1), []),
  };
}

/**
 * TODAY'S RUN OF A SAVED QUESTION (spec §16.4 primitive 1: re-opening RE-EXECUTES).
 * Idle while `id` is null. A 404 is the backend's single answer for "deleted" and
 * "a teammate's private row", and this hook does not invent a distinction.
 */
export function useSavedQuestionRun(id: string | null): {
  run: SavedRunBody | null;
  loading: boolean;
  notFound: boolean;
  error: string | null;
  reload: () => void;
} {
  const apiUrl = useApiUrl();
  const [run, setRun] = useState<SavedRunBody | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    setRun(null);
    setNotFound(false);
    setError(null);
    if (id === null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const response = await adminFetch(`${apiUrl}${AGENT_BASE}/saved-question/${id}/run`);
        const body = (await response.json()) as SavedRunBody;
        if (cancelled) return;
        setRun(body);
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof AdminApiError && caught.status === 404) setNotFound(true);
        else setError(messageOf(caught));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, id, reloadToken]);

  return {
    run,
    loading,
    notFound,
    error,
    reload: useCallback(() => setReloadToken((n) => n + 1), []),
  };
}
